import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import type { Filter, FollowUpAction, Statement } from 'src/types/myFollowUp';

import KpiSelection from './components/KpiSelection';
import MyFollowUp from './components/MyFollowUp';
import OrgUnitOverview from './components/OrgUnitOverview';
import useFollowUpDetail from './FollowUpDetailContext';

import { styles } from './FollowUpDetailModalContent.styles';

interface FollowUpDetailModalContentProps {
  closeModal: () => void;
  actionItem?: FollowUpAction;
  disabled?: boolean;
}

const FollowUpDetailModalContent: FC<FollowUpDetailModalContentProps> = ({
  closeModal,
  actionItem,
  disabled = false,
}) => {
  const { onSave, updateFormData, loading, kpis } = useFollowUpDetail();
  const { t } = useTranslation('myFollowUp');

  const [selectedKpi, setSelectedKpi] = useState<Filter | undefined>(undefined);
  const [selectedStatement, setSelectedStatement] = useState<
    Statement | undefined
  >(undefined);

  useEffect(() => {
    if (!loading && !!actionItem?.createdAt) {
      const foundKpi =
        kpis?.filters?.find((filter) => actionItem?.kpi?.id === filter.id) ||
        undefined;
      setSelectedKpi(foundKpi);
      setSelectedStatement(
        foundKpi?.statements.find(
          (statement) => actionItem?.statement?.id === statement.id,
        ) || undefined,
      );
    }
  }, [loading, actionItem, kpis]);

  useEffect(() => {
    updateFormData({
      kpi: selectedKpi,
      kpiValue1: selectedKpi?.indicator1 ?? null,
      kpiValue2: selectedKpi?.indicator2 ?? null,
      statement: selectedStatement,
      statementValue1: selectedStatement?.indicator1 ?? null,
      statementValue2: selectedStatement?.indicator2 ?? null,
    });
  }, [selectedKpi, selectedStatement]);

  if (loading || !actionItem) {
    return (
      <Stack
        direction="row"
        sx={[{ justifyContent: 'center' }, styles.contentWrapper]}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack direction="column" sx={styles.contentWrapper}>
      <Box sx={{ py: 4, flexGrow: 1 }}>
        <Stack direction="column" spacing={3} sx={styles.content}>
          <Accordion sx={styles.accordion} defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="primary" />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={styles.accordionSummary}
            >
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                {t('header.orgUnitOverview')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionContent}>
              <OrgUnitOverview />
            </AccordionDetails>
          </Accordion>

          <Accordion sx={styles.accordion} defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="primary" />}
              aria-controls="panel2-content"
              id="panel2-header"
              sx={styles.accordionSummary}
            >
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                {t('header.kpiSelection')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionContent}>
              <KpiSelection
                kpis={kpis}
                selectedKpi={selectedKpi}
                disabled={disabled}
                onKpiSelection={(kpi: Filter) => setSelectedKpi(kpi)}
                selectedStatement={selectedStatement}
                onStatementSelection={(statement: Statement) =>
                  setSelectedStatement(statement)
                }
              />
            </AccordionDetails>
          </Accordion>

          <Accordion sx={styles.accordion} defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="primary" />}
              aria-controls="panel3-content"
              id="panel3-header"
              sx={styles.accordionSummary}
            >
              <Typography
                component="span"
                variant="h6"
                sx={{ fontWeight: 700 }}
              >
                {t('header.myFollowUp')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionContent}>
              <MyFollowUp
                actionItem={actionItem}
                selectedKpi={selectedKpi}
                disabled={disabled}
              />
            </AccordionDetails>
          </Accordion>
          <Stack
            direction="column"
            sx={{ justifyContent: 'flex-end', flexGrow: 1 }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="cta"
                color="secondary"
                onClick={closeModal}
              >
                {t('followUp:cancel')}
              </Button>
              <Button
                disabled={disabled}
                fullWidth
                variant="cta"
                onClick={() => onSave()}
              >
                {t('followUp:save')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default FollowUpDetailModalContent;
